/**
 * Authentication store using Zustand.
 *
 * All authentication flows go through the backend API.
 * Tokens are stored in localStorage and used for API requests.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import * as authApi from '../services/auth-api'
import type { AuthUser, AuthTokens } from '../services/auth-api'
import { useOrgStore } from './org-store'

const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes before expiry

interface AuthState {
  user: AuthUser | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  onboardingCompleted: boolean
  onboardingSynced: boolean
  isLoading: boolean
  error: string | null

  // Actions
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, fullName?: string) => Promise<void>
  signOut: () => Promise<void>
  refreshTokenIfNeeded: () => Promise<void>
  getAccessToken: () => Promise<string | null>
  setOnboardingCompleted: (completed: boolean) => void
  syncOnboardingStatus: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      onboardingCompleted: false,
      onboardingSynced: false,
      isLoading: false,
      error: null,

      signIn: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await authApi.signIn({ email, password })

          if (!response.user) {
            throw new Error(
              (response as unknown as { detail?: string }).detail || 'Sign in failed'
            )
          }

          set({
            user: response.user,
            tokens: {
              access_token: response.access_token,
              refresh_token: response.refresh_token,
              expires_at: response.expires_at
            },
            isAuthenticated: true,
            onboardingCompleted: !!response.user.onboarding_completed,
            onboardingSynced: false, // will be synced on next app load
            isLoading: false
          })
        } catch (error: unknown) {
          // Extract the detail message from axios error response
          const axiosError = error as { response?: { data?: { detail?: string } } }
          const message =
            axiosError?.response?.data?.detail ||
            (error instanceof Error ? error.message : 'Sign in failed')
          set({
            error: message,
            isLoading: false
          })
          throw new Error(message)
        }
      },

      signUp: async (email: string, password: string, fullName?: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await authApi.signUp({ email, password, full_name: fullName })

          if (!response.user) {
            throw new Error(
              (response as unknown as { detail?: string }).detail ||
                'Sign up failed — please check your email to confirm your account'
            )
          }

          set({
            user: response.user,
            tokens: {
              access_token: response.access_token,
              refresh_token: response.refresh_token,
              expires_at: response.expires_at
            },
            isAuthenticated: true,
            onboardingCompleted: !!response.user.onboarding_completed,
            onboardingSynced: false,
            isLoading: false
          })
        } catch (error: unknown) {
          // Extract the detail message from axios error response
          const axiosError = error as { response?: { data?: { detail?: string } } }
          const message =
            axiosError?.response?.data?.detail ||
            (error instanceof Error ? error.message : 'Sign up failed')
          set({
            error: message,
            isLoading: false
          })
          throw new Error(message)
        }
      },

      signOut: async () => {
        set({ isLoading: true })

        try {
          await authApi.signOut()
        } catch {
          // Ignore errors - we're signing out anyway
        }

        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          onboardingCompleted: false,
          onboardingSynced: false,
          isLoading: false,
          error: null
        })
        useOrgStore.getState().clear()
      },

      refreshTokenIfNeeded: async () => {
        const { tokens } = get()

        if (!tokens) return

        // Check if token needs refresh
        const now = Date.now()
        const expiresAt = tokens.expires_at * 1000 // Convert to milliseconds

        if (expiresAt - now > TOKEN_REFRESH_THRESHOLD_MS) {
          // Token is still valid
          return
        }

        try {
          const response = await authApi.refreshToken(tokens.refresh_token)

          if (!response.user) {
            throw new Error('Token refresh failed — user data missing')
          }

          set({
            user: response.user,
            onboardingCompleted: !!response.user.onboarding_completed,
            tokens: {
              access_token: response.access_token,
              refresh_token: response.refresh_token,
              expires_at: response.expires_at
            }
          })
        } catch {
          // Refresh failed - sign out
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            onboardingCompleted: false,
            onboardingSynced: false
          })
          useOrgStore.getState().clear()
        }
      },

      getAccessToken: async () => {
        await get().refreshTokenIfNeeded()
        return get().tokens?.access_token || null
      },

      setOnboardingCompleted: (completed: boolean) => {
        set((state) => ({
          onboardingCompleted: completed,
          onboardingSynced: true,
          user: state.user ? { ...state.user, onboarding_completed: completed } : state.user
        }))
      },

      /**
       * Sync onboarding status from the backend.
       *
       * This ensures that if the user already completed onboarding in a
       * previous session (or on another device), the frontend reflects
       * the correct state and does not re-show the onboarding flow.
       *
       * Uses the unified /v1/me/status endpoint to fetch both profile
       * and organization data in a single call, avoiding race conditions.
       */
      syncOnboardingStatus: async () => {
        const { isAuthenticated, onboardingSynced } = get()
        const { orgsSynced } = useOrgStore.getState()
        // Re-sync when either auth or org bootstrap is missing.
        // orgsSynced is not persisted, while onboardingSynced is persisted.
        // Without this guard, a refreshed session can get stuck on an infinite spinner.
        if (!isAuthenticated || (onboardingSynced && orgsSynced)) return

        const SYNC_TIMEOUT_MS = 15_000

        try {
          // Dynamically import to avoid circular dependency
          const { profileApi } = await import('../services/profile-api')
          const status = await Promise.race([
            profileApi.getStatus(),
            new Promise<never>((_, reject) =>
              setTimeout(
                () => reject(new Error('Sync timeout')),
                SYNC_TIMEOUT_MS
              )
            ),
          ])
          
          const completed = !!status.onboarding_completed

          // Update auth store with onboarding status
          set((state) => ({
            onboardingCompleted: completed,
            onboardingSynced: true,
            user: state.user
              ? { ...state.user, onboarding_completed: completed }
              : state.user
          }))

          // Also populate org store with the fetched organizations
          // This avoids a separate fetchOrgs call and ensures consistency
          if (status.organizations && status.organizations.length > 0) {
            const mapped = status.organizations.map((row) => ({
              id: row.organization_id,
              name: row.organizations?.name || row.organization_id,
              slug: row.organizations?.slug || row.organization_id,
              role: row.role,
              business_category: row.organizations?.business_category ?? undefined,
              sample_list_query: row.organizations?.sample_list_query || undefined,
            }))
            
            // Find the active org based on active_organization_id from status
            let selected = null
            if (status.active_organization_id) {
              selected = mapped.find((m) => m.id === status.active_organization_id) || mapped[0] || null
            } else {
              selected = mapped[0] || null
            }
            
            // Update org store directly
            useOrgStore.setState({
              orgs: mapped,
              currentOrg: selected,
              isLoading: false,
              orgsSynced: true
            })
          } else {
            // User has no organizations - still mark as synced
            useOrgStore.setState({
              orgs: [],
              currentOrg: null,
              isLoading: false,
              orgsSynced: true
            })
          }
        } catch {
          // Profile may not exist yet — treat as not completed
          set({ onboardingSynced: true })
          // Also mark orgs as synced to avoid infinite loading
          useOrgStore.setState({ orgsSynced: true, isLoading: false })
        }
      },

      clearError: () => {
        set({ error: null })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        onboardingCompleted: state.onboardingCompleted,
        onboardingSynced: state.onboardingSynced
      })
    }
  )
)

// Set up token refresh interval
let refreshInterval: ReturnType<typeof setInterval> | null = null

useAuthStore.subscribe((state) => {
  if (state.isAuthenticated && !refreshInterval) {
    // Check token every minute
    refreshInterval = setInterval(() => {
      useAuthStore.getState().refreshTokenIfNeeded()
    }, 60 * 1000)
  } else if (!state.isAuthenticated && refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
})
